import clsx from 'clsx';
import { useMemo } from 'react';
import {
  Modal as ReactAriaModal,
  ModalOverlay as ReactAriaModalOverlay,
  ModalOverlayProps as ReactAriaModalOverlayProps,
} from 'react-aria-components';
import { ModalDialogContext } from './ModalDialogContext';
import styles from './OverlayBase.module.scss';

type OverlayBaseProps = Pick<
  ReactAriaModalOverlayProps,
  'children' | 'isDismissable' | 'isOpen' | 'onOpenChange'
> & {
  /** Class name applied to the dialog (the white box). */
  dialogClassName?: ReactAriaModalOverlayProps['className'];

  /** Inline styles applied to the dialog (the white box). */
  dialogStyle?: ReactAriaModalOverlayProps['style'];

  /** Class name applied to the overlay (the darkened backdrop). */
  overlayClassName?: ReactAriaModalOverlayProps['className'];

  /** Inline styles applied to the overlay (the darkened backdrop). */
  overlayStyle?: ReactAriaModalOverlayProps['style'];
};

/**
 * Base wrapper around React Aria's `ModalOverlay` and `Modal`components.
 * @private
 */
export const OverlayBase = ({
  children,
  dialogClassName,
  dialogStyle,
  isDismissable = true,
  isOpen,
  onOpenChange,
  overlayClassName,
  overlayStyle,
}: OverlayBaseProps) => {
  const contextValue = useMemo(() => ({ isDismissable }), [isDismissable]);

  return (
    <ModalDialogContext.Provider value={contextValue}>
      <ReactAriaModalOverlay
        className={(renderProps) => {
          const resolved =
            typeof overlayClassName === 'function'
              ? overlayClassName(renderProps)
              : overlayClassName;
          return clsx(styles.modalOverlay, resolved);
        }}
        data-zds
        isDismissable={isDismissable}
        isOpen={isOpen}
        isKeyboardDismissDisabled={!isDismissable}
        onOpenChange={onOpenChange}
        style={overlayStyle}
      >
        <ReactAriaModal
          className={(renderProps) => {
            const resolved =
              typeof dialogClassName === 'function'
                ? dialogClassName(renderProps)
                : dialogClassName;
            return clsx(styles.modal, resolved);
          }}
          style={dialogStyle}
          data-zds
        >
          {children}
        </ReactAriaModal>
      </ReactAriaModalOverlay>
    </ModalDialogContext.Provider>
  );
};
